import { CircularProgress, IconButton } from '@civilgrid/ui/components';
import { useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import {
  addCivilSearchMessage as apiAddCivilSearchMessage,
  addSingleFile as apiAddSingleFile,
  getCivilSearchMessages as apiGetMessages,
} from '~/api/civilsearch';
import { constructFile } from '~/common/miscUtils';
import { type CivilSearchMessage, FileCategory } from '~/common/types';
import MessageForm from '~/components2/Messages/MessageForm';
import { Notify } from '~/components2/Notify';
import store from '../../store';
import MessageList from './List';

export default function Chat() {
  const [messages, setMessages] = useState<CivilSearchMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { project } = useSnapshot(store) as typeof store;

  const query = useAsyncRetry(async () => {
    const data = await apiGetMessages(project!.id);
    setMessages([...data]);
  }, [project?.id]);

  const onSubmit = async (current: string, files: File[]) => {
    if (!current && !files.length) return;

    try {
      setIsProcessing(true);

      let fileResponses: any = [];

      if (files.length) {
        const uploads = files.map(async (f) => {
          const file = constructFile(f, {
            category: FileCategory.SupportingFile,
          });
          return await apiAddSingleFile(project!.id, file);
        });

        fileResponses = await Promise.all(uploads);
      }

      const message = { message: current, files: fileResponses };
      await apiAddCivilSearchMessage(project!.id, message);

      // refetch all messages for up to date data.
      query.retry();
    } catch {
      Notify.error({ message: 'An error occurred while sending the message' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (query.loading) return <CircularProgress indeterminate />;
  if (query.error) return <div>An error occurred while fetching messages.</div>;

  return (
    <div className="flex h-full grow flex-col justify-between space-y-4 overflow-y-clip">
      <div className="flex">
        <IconButton variant="secondary" icon="reset" onClick={query.retry}>
          Refresh Chat
        </IconButton>
      </div>
      <MessageList messages={messages} />
      <MessageForm onSubmit={onSubmit} isProcessing={isProcessing} />
    </div>
  );
}
